import {ReactNode} from 'react';
import {Alert, Avatar, Box, Button, Divider, Grid, Stack, Typography} from '@mui/material';
import {
  BusinessRounded,
  LoginRounded,
  LogoutRounded,
  MapRounded,
  OpenInNewRounded,
} from '@mui/icons-material';
import moment, {Moment} from 'moment';
import {useTranslation} from 'react-i18next';
import './attendance-registry-details-dialog.scss';
import {AttendanceLocationMap} from './attendance-location-map';
import {AttendanceManualNotice} from './attendance-manual-notice';
import {AttendanceVoidedNotice} from './attendance-voided-notice';
import AttendanceStatusChip from './attendance-status-chip';
import {AttendanceTrackerRegistrySession} from '../../backend/responses/attendance-tracker-registry';
import {AttendanceTrackerSessionPoint} from '../../backend/responses/attendance-tracker-session';
import {isTouchDevice} from '../../hooks/touch-device';
import {translations} from '../../i18n/translations';
import {Pallete} from '../../theme/bluebird';
import {
  formatRecordDateTime,
  formatSessionDuration,
  getSessionDurationSeconds,
  getSessionState,
} from '../../utils/functions/attendance-sessions';
import {Modal} from '../modal/modal';

type AttendanceRegistryDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  session?: AttendanceTrackerRegistrySession;
  entityName?: string;
  /** Picture of the entity */
  entityPicture?: string;
  /** If the session can be voided by the user: they can write the registry and it is not their own session */
  canVoid?: boolean;
  /** Called when the user wants to void the session. The reason is asked in another dialog. */
  onVoid?: (session: AttendanceTrackerRegistrySession) => void;
  /** Called when the user wants to correct the session. The new data is asked in another dialog. */
  onCorrect?: (session: AttendanceTrackerRegistrySession) => void;
  /** Current time according to the server, to know how long an open session is running */
  now: Moment;
};

/** A value to read: its label above and the value below it, without looking like a field that can be edited */
const ReadOnlyField = ({
  label,
  value,
  startAdornment,
}: {
  label: string;
  value?: string;
  startAdornment?: ReactNode;
}) => (
  <Box className="attendance-details-field">
    <Typography component="span" className="attendance-details-field-label">
      {label}
    </Typography>
    <Box className="attendance-details-field-value">
      {startAdornment}
      {value && <Typography>{value}</Typography>}
    </Box>
  </Box>
);

type MovementSectionProps = {
  type: 'entry' | 'exit';
  point?: AttendanceTrackerSessionPoint;
};

/** The entry or the exit of the session: when, where and the map of the location */
const MovementSection = ({type, point}: MovementSectionProps) => {
  const {t} = useTranslation();
  const texts = translations.daily_care_log.attendance_tracker;
  const isEntry = type === 'entry';
  const dateTime = point ? formatRecordDateTime(point.registeredAt, point.timeZone) : undefined;
  const Icon = isEntry ? LoginRounded : LogoutRounded;
  // Records registered by someone else have no location
  const location = point?.location;

  return (
    <Stack gap="20px">
      <Box className="attendance-details-section-title">
        <Icon sx={{color: isEntry ? Pallete.green.base : Pallete.red.base}} />
        <Typography>{t(isEntry ? texts.session.entry : texts.session.exit)}</Typography>
      </Box>

      {!point ? (
        <Typography className="attendance-details-empty">{t(texts.details.no_exit)}</Typography>
      ) : (
        <>
          {/* Who registered it, when it was registered by someone else on behalf of the professional */}
          <AttendanceManualNotice point={point} />

          <Grid container columnSpacing={2} rowSpacing={3}>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField label={t(texts.new.date_time)} value={dateTime?.text} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField label={t(texts.details.time_zone)} value={point.timeZone} />
            </Grid>
            {location ? (
              <>
                <Grid item xs={12} sm={6}>
                  <ReadOnlyField
                    label={t(texts.new.location)}
                    value={`${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ReadOnlyField
                    label={t(texts.new.accuracy)}
                    value={t(texts.new.accuracy_value, {meters: Math.round(location.accuracy)})}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <ReadOnlyField label={t(texts.new.location)} value={t(texts.manual.no_location)} />
              </Grid>
            )}
          </Grid>

          {location && (
            <>
              <AttendanceLocationMap
                latitude={location.latitude}
                longitude={location.longitude}
                accuracy={location.accuracy}
                // Fixed on touch screens, so the map does not get in the way when scrolling the dialog
                interactive={!isTouchDevice()}
                height={200}
              />
              <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MapRounded />}
                  endIcon={<OpenInNewRounded />}
                  href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{textTransform: 'none', fontWeight: 500}}
                >
                  {t(texts.details.open_in_maps)}
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Stack>
  );
};

/**
 * Everything about a work session in the registry: who and the state of the session as a whole, and then
 * the entry and the exit one below the other.
 */
export default function AttendanceRegistryDetailsDialog({
  open,
  onClose,
  session,
  entityName,
  entityPicture,
  canVoid,
  onVoid,
  onCorrect,
  now,
}: AttendanceRegistryDetailsDialogProps) {
  const {t} = useTranslation();
  const texts = translations.daily_care_log.attendance_tracker;
  const dialogTexts = translations.attendance_tracker_registry_screen.details;

  const state = session ? getSessionState(session, now) : 'closed';
  const isOverdue = state === 'overdue';
  const duration = session
    ? formatSessionDuration(getSessionDurationSeconds(session, now))
    : undefined;

  return (
    <Modal
      id="attendance-registry-details"
      title={t(dialogTexts.title)}
      open={open}
      onClose={onClose}
      actionButton={{type: 'close'}}
      fullWidth
      maxWidth="sm"
      readonly={!canVoid}
      actions={[
        // Voiding and correcting are done in their own dialogs, where the reason is asked
        ...(canVoid && session
          ? [
              {
                label: t(dialogTexts.void_action),
                type: 'destructive' as const,
                onClick: () => onVoid?.(session),
              },
              {
                label: t(dialogTexts.correct_action),
                type: 'neutral' as const,
                onClick: () => onCorrect?.(session),
              },
            ]
          : []),
        {label: t(dialogTexts.close), type: 'nondestructive' as const, onClick: onClose},
      ]}
    >
      {session && (
        <Stack gap="20px" className="attendance-registry-details">
          <Grid container columnSpacing={2} rowSpacing={3}>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField
                label={t(texts.new.professional)}
                value={session.user.name}
                startAdornment={
                  <Avatar
                    src={session.user.picture}
                    alt={session.user.name}
                    sx={{width: 22, height: 22}}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField
                label={t(texts.new.entity)}
                value={entityName}
                startAdornment={
                  <Avatar src={entityPicture} alt={entityName} sx={{width: 22, height: 22}}>
                    <BusinessRounded sx={{fontSize: 14}} />
                  </Avatar>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField
                label={t(texts.filters.sections.attendanceStatus)}
                startAdornment={<AttendanceStatusChip state={state} />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField
                label={t(dialogTexts.duration)}
                value={state === 'voided' ? '-' : duration}
              />
            </Grid>
          </Grid>

          {/* Who voided it and why: it does not count anymore */}
          <AttendanceVoidedNotice voided={session.voided} />

          {isOverdue && (
            <Alert severity="error">
              {t(texts.session.overdue_alert, {
                date: moment(session.entry.registeredAt).format('DD/MM/YYYY HH:mm'),
              })}
            </Alert>
          )}

          <Divider />
          <MovementSection type="entry" point={session.entry} />
          <Divider />
          <MovementSection type="exit" point={session.exit} />
        </Stack>
      )}
    </Modal>
  );
}
